import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type {
  ApiClient, DatevAbgleich, DatevAusgang, DatevKonten, DatevRueckweg, DatevZahlungen,
  Unternehmen,
} from "@kundeninfo/api-client";
import { Feld, Modal, ModalFuss, darfAbbrechen, useFormularZustand } from "@kundeninfo/ui";
import { Auswahl, Formular, Hinweis, Register, Reiter as MeReiter, Tabelle }
  from "@kundeninfo/ui/me-design";
import { Aufklapper, useKopffaltung } from "../components/Kopffaltung.js";
import { KiKnopf, Dateiwahl } from "../components/meHuellen.js";
import { apiMeldung, speichereBlob } from "../components/vorgangsdetail.js";
import { formatBetrag, formatDatum, formatZeitpunkt } from "../format.js";
import "./DatevKopplung.css";

/**
 * SCHNITTSTELLEN → DATEV (Spec, Abschnitt „Oberfläche") — drei Abschnitte unter EINEM
 * Filterkopf: „Debitoren" (Abgleich), „Ausgang" (Journal und Stapel), „Rückweg" (Importe
 * und Zahlungen).
 *
 * **Nur für ADMIN** (Ruling D23) — durchgesetzt wird das am SERVER (403); diese Maske
 * zeigt bei 403 ein Band und keine leere Liste. Eine eigene Rollenprüfung hier wäre eine
 * zweite Wahrheit.
 *
 * **EIN Filterkopf je Unternehmen.** Jedes Konzernunternehmen ist bei DATEV ein eigener
 * Mandant; alles auf dieser Seite hängt an genau einem. Der Wechsel setzt ZUERST alles
 * zurück (`lauf`-Ref-Muster) und lädt dann neu: Eine Abgleichsliste des einen
 * Unternehmens über den Zahlen des anderen wäre die gefährlichste Art von falsch.
 *
 * **Jeder datenbehaftete Schreibgriff läuft über ein STRIKTES Modalpanel** (Hausregel):
 * „Datei einlesen", „Nummer eintragen", „Kunden anlegen", „Stapel erzeugen",
 * „Bestätigen", „Verwerfen". Die beiden EIN-KLICK-Handgriffe ohne eigene Eingabe —
 * „Nächste Nummer" (zieht aus dem Kreis) und „Ignorieren"/„Übernehmen"/„Zurücknehmen" an
 * einer bereits eindeutigen Zeile — bleiben ohne Panel, genau wie „Ignorieren" im
 * Aircall-Dienst: Sie tragen kein Feld, das eine Fehleingabe tragen könnte, und
 * „Zurücknehmen" hält den einzigen der drei Fälle ohnehin umkehrbar.
 *
 * **SCHLECHTESTES OBEN** — die Reihenfolge kommt vom SERVER (`abgleiche` in der Domäne,
 * `journalLesen`/`zahlungsListe` im Repository) und wird hier NICHT noch einmal sortiert.
 * Zwei Sortierungen liefen beim ersten neuen Zustand auseinander.
 *
 * Der CSS-Block heisst `dtv` und gehört diesem Paket allein.
 *
 * **ME-Bausteine (ME-Etappe 3, Aufgabe 7).** Filterkopf `Auswahl`, Abschnitte in
 * `Register`/`Reiter` (Muster LayoutRegister/KundenAkte), die vier Tabellen in `Tabelle`
 * (Zahlen rechtsbündig über `data-ausrichtung`), Fehler- und Statuszeilen als `Hinweis`,
 * Datumsfelder als `Feld` im `Formular`. Die Bestätigungsdialoge bleiben strikte `Modal`
 * mit dem Fehler IM Dialog; „Nummer eintragen" läuft über `useFormularZustand`.
 * Die beiden Datei-Uploads sind seit ME-Etappe 4 (P4b) `me-eingabe art="datei"` (Entscheid 31)
 * über `Dateiwahl` — im `Formular` beschriftet wie ein `Feld`, Pflicht, während des Einlesens
 * gesperrt.
 */

export const DATEV_SCHLUESSEL = "datev";
export const DATEV_NAME = "DATEV";

/** Nur die Wege, die dieser Bereich braucht — als `Pick`, damit der Test ihn mit ein paar
 *  `vi.fn` zeichnen kann (Muster `AircallClient`). Die Methodennamen sind die des
 *  ERZEUGTEN Klienten (Aufgabe 8) — sie gewinnen gegen den Umsetzungsplan.
 *  `holeDatevStapelPaket` fehlt bewusst: Das Paket wird als LINK auf den GET-Weg
 *  heruntergeladen (der Browser lädt), nicht per fetch+blob — siehe der Knopf im
 *  Ausgang-Abschnitt. */
export type DatevClient = Pick<ApiClient,
  | "getDatevAbgleich" | "einlesenDatevDebitoren" | "verwerfeDatevDebitorenLauf"
  | "legeKundeAusDatevDebitorAn" | "ignoriereDatevAbgleichZeile" | "exportiereDatevDebitoren"
  | "getDatevAusgang" | "erzeugeDatevStapel" | "bestaetigeDatevStapel"
  | "getDatevRueckweg" | "einlesenDatevBuchungen" | "verwerfeDatevBuchungsLauf"
  | "getDatevZahlungsliste" | "datevZahlungUebernehmen" | "getDatevKonten"
  | "setDebitorennummer">;

export type DatevBereichProps = {
  client: DatevClient;
  /** Die sichtbaren Konzernunternehmen — der Filterkopf. */
  companies: readonly Unternehmen[];
  /** Das gewählte Unternehmen; `null` heisst „noch keines". */
  companyId: string | null;
  onUnternehmen: (companyId: string) => void;
};

type Reiter = "DEBITOREN" | "AUSGANG" | "RUECKWEG";

const REITER: readonly Reiter[] = ["DEBITOREN", "AUSGANG", "RUECKWEG"];

const REITER_LABEL: Record<Reiter, string> = {
  DEBITOREN: "Debitoren", AUSGANG: "Ausgang", RUECKWEG: "Rückweg",
};

/** Id des Panels zu einem Abschnitt — zugleich die Kennung seines `me-reiter` (`fuer`):
 *  Das Register meldet sie in `me-reiter-gewaehlt` und erwartet sie in `aktiv`. */
const REITER_ID: Record<Reiter, string> = {
  DEBITOREN: "dtv-abschnitt-debitoren", AUSGANG: "dtv-abschnitt-ausgang",
  RUECKWEG: "dtv-abschnitt-rueckweg",
};

/** Alle Panels dieser Seite in EINER Aufzählung, damit nie zwei gleichzeitig offen sind. */
type Dialog =
  | { art: "DEBITOREN_EINLESEN"; standAt: string; datei: File | null }
  | { art: "NUMMER_EINTRAGEN"; customerId: string; relationId: string;
      name: string; debtorNumber: string }
  | { art: "KUNDE_ANLEGEN"; debtorNumber: string; name: string }
  | { art: "STAPEL_ERZEUGEN"; von: string; bis: string }
  | { art: "STAPEL_BESTAETIGEN"; batchId: string; zeitraum: string }
  | { art: "BUCHUNGEN_EINLESEN"; standAt: string; von: string; bis: string;
      datei: File | null }
  | { art: "LAUF_VERWERFEN"; importId: string; uebernommen: number };

const POSTEN_STATUS_LABEL: Record<DatevAusgang["posten"][number]["status"], string> = {
  FAILED: "Fehlgeschlagen", OPEN: "Offen", EXPORTED: "Übergeben", CONFIRMED: "Bestätigt",
};

const ABGLEICH_ART_LABEL: Record<DatevAbgleich["zeilen"][number]["art"], string> = {
  KUNDE_OHNE_DEBITOR: "Kunde ohne Debitor", ABWEICHUNG: "Abweichung",
  DEBITOR_OHNE_KUNDE: "Debitor ohne Kunde",
};

const ABWEICHUNG_FELD_LABEL: Record<"name" | "anschrift" | "vatId", string> = {
  name: "Name", anschrift: "Anschrift", vatId: "USt-IdNr.",
};

/** Eine Dezimal-Zeichenkette der API als Euro-Betrag — DATEV-Beträge dieses Pakets sind
 *  ausnahmslos EUR (Kontenrahmen SKR03/SKR04 kennen keine Fremdwährung). */
function euro(wert: string): string {
  const zahl = Number(wert);
  return Number.isFinite(zahl) ? formatBetrag(zahl, "EUR") : wert;
}

export function DatevBereich(p: DatevBereichProps): ReactElement {
  const [reiter, setReiter] = useState<Reiter>("DEBITOREN");
  const [abgleich, setAbgleich] = useState<DatevAbgleich | null>(null);
  const [ausgang, setAusgang] = useState<DatevAusgang | null>(null);
  const [rueckweg, setRueckweg] = useState<DatevRueckweg | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [verboten, setVerboten] = useState(false);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [dialogFehler, setDialogFehler] = useState<string | null>(null);
  const [beschaeftigt, setBeschaeftigt] = useState(false);

  /** Der Riegel gegen Antworten, die zu spät kommen (Muster `KundePortalMandanten`). */
  const lauf = useRef(0);
  const companyId = p.companyId;
  const client = p.client;

  const laden = useCallback(async (): Promise<void> => {
    if (companyId === null) return;
    const meiner = (lauf.current += 1);
    try {
      const [a, b, c] = await Promise.all([
        client.getDatevAbgleich(companyId),
        client.getDatevAusgang(companyId),
        client.getDatevRueckweg(companyId),
      ]);
      if (meiner !== lauf.current) return;
      setAbgleich(a); setAusgang(b); setRueckweg(c); setMeldung(null); setVerboten(false);
    } catch (fehler) {
      if (meiner !== lauf.current) return;
      setAbgleich(null); setAusgang(null); setRueckweg(null);
      if (fehler instanceof ApiError && fehler.status === 403) {
        setVerboten(true); setMeldung(null);
      } else {
        setVerboten(false);
        setMeldung(apiMeldung(fehler, "Die DATEV-Daten liessen sich nicht laden."));
      }
    }
  }, [companyId, client]);

  /* KEIN ZUSTAND ÜBERLEBT DEN WECHSEL (Hausregel): zuerst alles weg, dann laden. */
  useEffect(() => {
    lauf.current += 1;
    setAbgleich(null); setAusgang(null); setRueckweg(null);
    setDialog(null); setDialogFehler(null); setMeldung(null); setVerboten(false);
    setReiter("DEBITOREN");
    void laden();
    return () => { lauf.current += 1; };
  }, [companyId, laden]);

  function schliessen(): void {
    if (beschaeftigt) return;
    setDialog(null); setDialogFehler(null);
  }

  /** Nach jedem Schreibgriff wird ALLES neu gelesen (Prioritätenkette: Funktion vor
   *  Performance) — der Abgleich, das Journal und der Rückweg hängen an denselben
   *  Datenständen (eine neu vergebene Debitorennummer etwa verändert sowohl die
   *  Abgleichsliste als auch, beim nächsten Stapel, den Ausgang). */
  async function nachSchreiben(): Promise<void> {
    setDialog(null); setDialogFehler(null);
    await laden();
  }

  if (companyId === null) {
    return <p className="dtv__hinweis">Kein Unternehmen gewählt.</p>;
  }

  return (
    <div className="dtv">
      <div className="dtv__kopf">
        <Auswahl id="dtv-unternehmen" name="dtv-unternehmen" className="dtv__unternehmen"
          beschriftung="Unternehmen" wert={companyId}
          onGeaendert={(d) => p.onUnternehmen(d.wert)}>
          {p.companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </Auswahl>
      </div>

      {/* Eine Rechteauskunft, kein Fehlschlag: `warnung` (Review A7). Schlechtestes oben —
          eine echte Fehlermeldung (`meldung`) erscheint nie zugleich (403 räumt sie). */}
      {verboten && (
        <Hinweis art="warnung">
          Dieser Bereich steht nur Administratorinnen und Administratoren offen.
        </Hinweis>
      )}
      {meldung !== null && <Hinweis art="fehler">{meldung}</Hinweis>}

      {!verboten && (
        <>
          {/* Die Reiterleiste seit ME-Etappe 3 (Aufgabe 7): `me-register` mit einem
              `me-reiter` je Abschnitt (Muster LayoutRegister). `aria-label` am Host
              spiegelt das Element auf seine tablist im Shadow DOM; Pfeiltasten, Pos1/Ende
              und „Reiter nach unten offen" bringt das Element mit. Der aktive Reiter folgt
              dem Zustand (`aktiv`) — der Unternehmenswechsel setzt ihn auf „Debitoren"
              zurück. Kein Fehlerzähler: Die Abschnitte tragen keine Feldbefunde. */}
          <Register className="dtv__register" aria-label="DATEV-Bereich"
            aktiv={REITER_ID[reiter]}
            onReiterGewaehlt={(d) => {
              const gewaehlt = REITER.find((r) => REITER_ID[r] === d.id);
              if (gewaehlt !== undefined) setReiter(gewaehlt);
            }}>
            {REITER.map((r) => (
              <MeReiter key={r} fuer={REITER_ID[r]} beschriftung={REITER_LABEL[r]} />
            ))}
          </Register>

          {/* Das Panel trägt seinen Namen selbst: `fuer` setzt `aria-controls` im Shadow
              DOM, das über die Schattengrenze nicht auf dieses Panel zeigt. */}
          <div id={REITER_ID[reiter]} role="tabpanel" aria-label={REITER_LABEL[reiter]}
            className="dtv__registerinhalt">
          {reiter === "DEBITOREN" && (
            <DebitorenAbschnitt companyId={companyId} client={client} abgleich={abgleich}
              onOeffnen={setDialog} onFehler={setMeldung} onNeuLaden={() => void laden()}
              beschaeftigt={beschaeftigt} setBeschaeftigt={setBeschaeftigt} />
          )}
          {reiter === "AUSGANG" && (
            <AusgangAbschnitt companyId={companyId} ausgang={ausgang}
              onOeffnen={setDialog} />
          )}
          {reiter === "RUECKWEG" && (
            <RueckwegAbschnitt companyId={companyId} client={client} rueckweg={rueckweg}
              onOeffnen={setDialog} />
          )}
          </div>
        </>
      )}

      {dialog !== null && (
        <DialogPanel dialog={dialog} companyId={companyId} client={client}
          fehler={dialogFehler} beschaeftigt={beschaeftigt}
          setBeschaeftigt={setBeschaeftigt} setFehler={setDialogFehler}
          onAbbrechen={schliessen} onFertig={() => { void nachSchreiben(); }} />
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------------------- */
/* Abschnitt „Debitoren"                                                                   */
/* -------------------------------------------------------------------------------------- */

type DebitorenAbschnittProps = {
  companyId: string;
  client: DatevClient;
  abgleich: DatevAbgleich | null;
  onOeffnen: (d: Dialog) => void;
  onFehler: (m: string) => void;
  onNeuLaden: () => void;
  beschaeftigt: boolean;
  setBeschaeftigt: (b: boolean) => void;
};

function DebitorenAbschnitt(p: DebitorenAbschnittProps): ReactElement {
  const { abgleich } = p;

  async function naechsteNummer(customerId: string, relationId: string): Promise<void> {
    p.setBeschaeftigt(true);
    try {
      await p.client.setDebitorennummer(customerId, relationId, { modus: "NAECHSTE" });
      p.onNeuLaden();
    } catch (e) {
      p.onFehler(apiMeldung(e, "Die nächste Nummer liess sich nicht vergeben."));
    } finally {
      p.setBeschaeftigt(false);
    }
  }

  async function ignorieren(ziel: { relationId: string } | { debtorNumber: string })
    : Promise<void> {
    p.setBeschaeftigt(true);
    try {
      await p.client.ignoriereDatevAbgleichZeile(p.companyId, ziel);
      p.onNeuLaden();
    } catch (e) {
      p.onFehler(apiMeldung(e, "Das Ignorieren ist nicht durchgekommen."));
    } finally {
      p.setBeschaeftigt(false);
    }
  }

  async function exportieren(): Promise<void> {
    try {
      const { blob, filename } = await p.client.exportiereDatevDebitoren(p.companyId);
      speichereBlob(blob, filename ?? "neue-debitoren.csv");
    } catch (e) {
      p.onFehler(apiMeldung(e, "Der Export ist nicht durchgekommen."));
    }
  }

  return (
    <section className="dtv__abschnitt">
      <div className="dtv__abschnitt-kopf">
        <p className="dtv__hinweis">
          {abgleich?.stand === null || abgleich === null
            ? "Noch keine Debitorenliste eingelesen."
            : `Debitorenliste vom ${formatDatum(abgleich.stand.standAt)}, `
              + `${abgleich.stand.rowCount} `
              + `${abgleich.stand.rowCount === 1 ? "Zeile" : "Zeilen"}.`}
        </p>
        <div className="dtv__aktionen">
          <KiKnopf onClick={() => p.onOeffnen({ art: "DEBITOREN_EINLESEN", standAt: "",
              datei: null })}>
            Datei einlesen
          </KiKnopf>
          <KiKnopf onClick={() => { void exportieren(); }}>
            Neue Debitoren exportieren
          </KiKnopf>
        </div>
      </div>

      {abgleich === null ? (
        <p className="dtv__hinweis" role="status">Lädt …</p>
      ) : abgleich.zeilen.length === 0 ? (
        <p className="dtv__hinweis">
          Alles im Gleichklang — kein Kunde ohne Debitor, kein fremder Debitor, keine
          Abweichung.
        </p>
      ) : (
        /* me-tabelle (ME-Etappe 3, Aufgabe 7): Rahmen, Kopfzeile, Linien, Zebra und das
           waagerechte Rollen bringt das Element; die Zeilenaktionen bleiben, wie sie sind. */
        <Tabelle stapeln className="dtv__tabellenrahmen">
        <table className="dtv__tabelle">
          <thead>
            <tr>
              <th scope="col">Art</th><th scope="col">Name</th><th scope="col">Nummer</th>
              <th scope="col">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {abgleich.zeilen.map((z, i) => {
              const name = z.kunde?.companyName ?? z.debitor?.name ?? "—";
              const beschriftung = `${ABGLEICH_ART_LABEL[z.art]}, ${name}`;
              return (
                <tr key={`${z.art}-${z.relationId ?? z.debtorNumber ?? i}`}
                  className={z.art === "KUNDE_OHNE_DEBITOR"
                    ? "dtv__zeile dtv__zeile--schlecht"
                    : z.art === "ABWEICHUNG" ? "dtv__zeile dtv__zeile--warn" : "dtv__zeile"}
                  aria-label={beschriftung}>
                  <td data-label="Art">{ABGLEICH_ART_LABEL[z.art]}</td>
                  <td data-label="Name">
                    {name}
                    {z.art === "ABWEICHUNG" && z.abweichungen.length > 0 && (
                      <ul className="dtv__abweichungen">
                        {z.abweichungen.map((a) => (
                          <li key={a.feld}>
                            {ABWEICHUNG_FELD_LABEL[a.feld]}: <span className="dtv__wert">
                              unser Stand „{a.unsere}"</span>
                            {" · "}
                            <span className="dtv__ihrwert">DATEV „{a.ihre}"</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {z.art === "ABWEICHUNG" && (
                      <p className="dtv__hinweis">
                        Die Anschrift wird in der Kundenakte gepflegt.
                        {z.kunde !== null && (
                          <> {" "}
                            {/* Ein echter `<a href>` und KEIN `onOeffnen`-Rückruf wie an
                                anderen internen Sprüngen (Muster „Teil von …" in
                                KundenAkte.tsx): Der Bereich Schnittstellen kennt die
                                Kundenakte nicht als Ziel, das er ansteuern könnte — dafür
                                bräuchte `Schnittstellen.tsx` einen eigenen Rückruf bis
                                App.tsx hinauf, für einen einzelnen Verweis in einer
                                Randspalte. Der Klick lädt die Seite neu; das ist der
                                Preis, nicht ein Versehen. */}
                            <a href={`/kunden/${z.kunde.customerId}`}>Kunde anzeigen</a>
                          </>
                        )}
                      </p>
                    )}
                  </td>
                  <td data-label="Nummer">{z.debtorNumber ?? "—"}</td>
                  <td data-label="Aktionen">
                    <div className="dtv__aktionen">
                      {z.art === "KUNDE_OHNE_DEBITOR" && z.kunde !== null && (
                        <>
                          <KiKnopf deaktiviert={p.beschaeftigt} onClick={() => { void naechsteNummer(
                              z.kunde!.customerId, z.kunde!.relationId); }}>
                            Nächste Nummer
                          </KiKnopf>
                          <KiKnopf onClick={() => p.onOeffnen({ art: "NUMMER_EINTRAGEN",
                              customerId: z.kunde!.customerId,
                              relationId: z.kunde!.relationId,
                              name: z.kunde!.companyName, debtorNumber: "" })}>
                            Nummer eintragen
                          </KiKnopf>
                          <KiKnopf deaktiviert={p.beschaeftigt} onClick={() => { void ignorieren(
                              { relationId: z.kunde!.relationId }); }}>
                            Ignorieren
                          </KiKnopf>
                        </>
                      )}
                      {z.art === "DEBITOR_OHNE_KUNDE" && z.debitor !== null && (
                        <>
                          <KiKnopf art="primaer" onClick={() => p.onOeffnen({ art: "KUNDE_ANLEGEN",
                              debtorNumber: z.debitor!.debtorNumber,
                              name: z.debitor!.name })}>
                            Kunden anlegen
                          </KiKnopf>
                          <KiKnopf deaktiviert={p.beschaeftigt} onClick={() => { void ignorieren(
                              { debtorNumber: z.debitor!.debtorNumber }); }}>
                            Ignorieren
                          </KiKnopf>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </Tabelle>
      )}
    </section>
  );
}

/* -------------------------------------------------------------------------------------- */
/* Abschnitt „Ausgang"                                                                     */
/* -------------------------------------------------------------------------------------- */

type AusgangAbschnittProps = {
  companyId: string;
  ausgang: DatevAusgang | null;
  onOeffnen: (d: Dialog) => void;
};

function AusgangAbschnitt(p: AusgangAbschnittProps): ReactElement {
  const { ausgang, companyId } = p;
  return (
    <section className="dtv__abschnitt">
      <div className="dtv__abschnitt-kopf">
        <KiKnopf art="primaer" onClick={() => p.onOeffnen({ art: "STAPEL_ERZEUGEN", von: "", bis: "" })}>
          Stapel erzeugen
        </KiKnopf>
      </div>

      {ausgang === null ? (
        <p className="dtv__hinweis" role="status">Lädt …</p>
      ) : (
        <>
          <h4 className="dtv__untertitel">Stapel</h4>
          {ausgang.stapel.length === 0 ? (
            <p className="dtv__hinweis">Noch kein Stapel erzeugt.</p>
          ) : (
            <ul className="dtv__stapelliste">
              {ausgang.stapel.map((s) => (
                <li key={s.id} className="dtv__stapel">
                  <span>
                    {formatDatum(s.periodStart)} – {formatDatum(s.periodEnd)}
                    {/* „Posten" bleibt im Deutschen auch in der Mehrzahl unverändert —
                        anders als sonst in dieser Anwendung also KEIN Fallunterschied
                        nach Anzahl (Fixrunde 1, Befund M1). */}
                    {" · "}{s.postenCount} Posten
                  </span>
                  {s.missingDocs > 0 && (
                    <span className="dtv__warnung">
                      {s.missingDocs} {s.missingDocs === 1 ? "Beleg fehlt" : "Belege fehlen"}
                    </span>
                  )}
                  <div className="dtv__aktionen">
                    {/* ZIP-Download als LINK auf den GET-Weg — der Browser lädt selbst,
                        kein fetch+blob (Weisung zu diesem Paket): Das Paket kann Belege
                        als PDF/ZUGFeRD enthalten und muss nicht vollständig in den
                        Speicher der Seite geladen werden, nur um es sofort wieder
                        auszuliefern. */}
                    {/* Seit me-design 8f7e53e (Befund P6-1) ein me-knopf mit `href`: innen
                        ein echter Link im Knopfbild, `download` mit leerem Namen nimmt den
                        Dateinamen aus der Antwort; `_blank` setzt rel="noopener". */}
                    <KiKnopf href={`/api/datev/${encodeURIComponent(companyId)}/ausgang/stapel/`
                        + `${encodeURIComponent(s.id)}/paket`}
                      download="" ziel="_blank">
                      Paket herunterladen
                    </KiKnopf>
                    {s.confirmedAt === null ? (
                      <KiKnopf onClick={() => p.onOeffnen({ art: "STAPEL_BESTAETIGEN",
                          batchId: s.id,
                          zeitraum: `${formatDatum(s.periodStart)} – ${formatDatum(s.periodEnd)}` })}>
                        Bestätigen
                      </KiKnopf>
                    ) : (
                      <span className="dtv__hinweis">
                        Bestätigt am {formatZeitpunkt(s.confirmedAt)}
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}

          <h4 className="dtv__untertitel">Journal</h4>
          {ausgang.posten.length === 0 ? (
            <p className="dtv__hinweis">Noch kein Posten entstanden.</p>
          ) : (
            <Tabelle stapeln className="dtv__tabellenrahmen">
            <table className="dtv__tabelle">
              <thead>
                <tr>
                  <th scope="col">Rechnung</th><th scope="col">Kunde</th>
                  <th scope="col" data-ausrichtung="rechts">Betrag</th>
                  <th scope="col">Status</th>
                </tr>
              </thead>
              <tbody>
                {ausgang.posten.map((post) => (
                  <tr key={post.id}
                    className={post.status === "FAILED"
                      ? "dtv__zeile dtv__zeile--schlecht"
                      : post.status === "OPEN" ? "dtv__zeile dtv__zeile--warn"
                        : "dtv__zeile"}
                    aria-label={`Rechnung ${post.invoiceNumber ?? "Entwurf"}, `
                      + `${post.customerName}`}>
                    <td data-label="Rechnung">{post.invoiceNumber ?? "—"}</td>
                    <td data-label="Kunde">{post.customerName}</td>
                    <td data-label="Betrag" data-ausrichtung="rechts">
                      {euro(post.netAmount)}
                    </td>
                    <td data-label="Status">
                      {POSTEN_STATUS_LABEL[post.status]}
                      {post.errorText !== null && (
                        <p className="dtv__hinweis">{post.errorText}</p>
                      )}
                      {post.debtorNumber === null && (
                        <p className="dtv__hinweis">Es fehlt eine Debitorennummer.</p>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </Tabelle>
          )}
        </>
      )}
    </section>
  );
}

/* -------------------------------------------------------------------------------------- */
/* Abschnitt „Rückweg"                                                                     */
/* -------------------------------------------------------------------------------------- */

type RueckwegAbschnittProps = {
  companyId: string;
  client: DatevClient;
  rueckweg: DatevRueckweg | null;
  onOeffnen: (d: Dialog) => void;
};

function RueckwegAbschnitt(p: RueckwegAbschnittProps): ReactElement {
  const { rueckweg } = p;
  return (
    <section className="dtv__abschnitt">
      <div className="dtv__abschnitt-kopf">
        <KiKnopf art="primaer" onClick={() => p.onOeffnen({ art: "BUCHUNGEN_EINLESEN", standAt: "", von: "",
            bis: "", datei: null })}>
          Datei einlesen
        </KiKnopf>
      </div>

      {rueckweg === null ? (
        <p className="dtv__hinweis" role="status">Lädt …</p>
      ) : rueckweg.laeufe.length === 0 ? (
        <p className="dtv__hinweis">Noch keine Buchungsdatei eingelesen.</p>
      ) : (
        <ul className="dtv__laeufe">
          {rueckweg.laeufe.map((l) => (
            <RueckwegLaufZeile key={l.id} companyId={p.companyId} client={p.client}
              lauf={l} onOeffnen={p.onOeffnen} />
          ))}
        </ul>
      )}
    </section>
  );
}

function RueckwegLaufZeile(p: {
  companyId: string; client: DatevClient; lauf: DatevRueckweg["laeufe"][number];
  onOeffnen: (d: Dialog) => void;
}): ReactElement {
  const { lauf } = p;
  const zahlungenFaltung = useKopffaltung(false);
  const kontenFaltung = useKopffaltung(false);

  return (
    <li className="dtv__lauf">
      <p className="dtv__hinweis">
        Stand {formatDatum(lauf.standAt)} · {formatDatum(lauf.periodStart)} – {" "}
        {formatDatum(lauf.periodEnd)} · {lauf.rowCount} Zeilen · {lauf.uebernommen} übernommen
      </p>

      <Aufklapper faltung={zahlungenFaltung} titel="Zahlungen zuordnen"
        inhaltId={`dtv-zahlungen-${lauf.id}`}>
        {zahlungenFaltung.offen && (
          <ZahlungenInhalt companyId={p.companyId} client={p.client} importId={lauf.id} />
        )}
      </Aufklapper>

      <Aufklapper faltung={kontenFaltung} titel="Konten anzeigen"
        inhaltId={`dtv-konten-${lauf.id}`}>
        {kontenFaltung.offen && (
          <KontenInhalt companyId={p.companyId} client={p.client} importId={lauf.id} />
        )}
      </Aufklapper>

      <div className="dtv__aktionen">
        <KiKnopf onClick={() => p.onOeffnen({ art: "LAUF_VERWERFEN", importId: lauf.id,
            uebernommen: lauf.uebernommen })}>
          Verwerfen
        </KiKnopf>
      </div>
    </li>
  );
}

function ZahlungenInhalt(p: { companyId: string; client: DatevClient; importId: string })
  : ReactElement {
  const [seite, setSeite] = useState<DatevZahlungen | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let abgebrochen = false;
    p.client.getDatevZahlungsliste(p.companyId, p.importId)
      .then((antwort) => { if (!abgebrochen) { setSeite(antwort); setFehler(null); } })
      .catch((e: unknown) => {
        if (abgebrochen) return;
        setSeite(null);
        setFehler(apiMeldung(e, "Die Zahlungsliste konnte nicht geladen werden."));
      });
    return () => { abgebrochen = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- companyId/importId/client
    // sind über den Schlüssel des Aufklappers bereits stabil je Lauf.
  }, [p.companyId, p.importId, p.client, version]);

  async function uebernehmen(bookingId: string, zuruecknehmen?: boolean): Promise<void> {
    try {
      await p.client.datevZahlungUebernehmen(p.companyId, bookingId, zuruecknehmen);
      setVersion((v) => v + 1);
    } catch (e) {
      setFehler(apiMeldung(e, "Die Zahlung liess sich nicht zuordnen."));
    }
  }

  if (fehler !== null) return <Hinweis art="fehler">{fehler}</Hinweis>;
  if (seite === null) return <p className="dtv__hinweis" role="status">Lädt …</p>;
  if (seite.zeilen.length === 0) {
    return <p className="dtv__hinweis">Keine Buchung dieses Laufs lässt sich zuordnen.</p>;
  }

  return (
    <Tabelle stapeln className="dtv__tabellenrahmen">
    <table className="dtv__tabelle">
      <thead>
        <tr>
          <th scope="col">Rechnung</th><th scope="col" data-ausrichtung="rechts">Betrag</th>
          <th scope="col">Beleg</th><th scope="col">Aktion</th>
        </tr>
      </thead>
      <tbody>
        {seite.zeilen.map((z) => (
          <tr key={z.bookingId}
            className={!z.eindeutig ? "dtv__zeile dtv__zeile--warn" : "dtv__zeile"}
            aria-label={`Buchung ${z.invoiceNumber}`}>
            <td data-label="Rechnung">{z.invoiceNumber}</td>
            <td data-label="Betrag" data-ausrichtung="rechts">{euro(z.betrag)}</td>
            <td data-label="Beleg">{formatDatum(z.belegdatum)}</td>
            <td data-label="Aktion">
              {z.uebernommen ? (
                <KiKnopf onClick={() => { void uebernehmen(z.bookingId, true); }}>
                  Zurücknehmen
                </KiKnopf>
              ) : z.eindeutig ? (
                <KiKnopf art="primaer" onClick={() => { void uebernehmen(z.bookingId); }}>
                  Übernehmen
                </KiKnopf>
              ) : (
                <span className="dtv__hinweis">{z.grund ?? "Nicht eindeutig"}</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    </Tabelle>
  );
}

function KontenInhalt(p: { companyId: string; client: DatevClient; importId: string })
  : ReactElement {
  const [konten, setKonten] = useState<DatevKonten | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    let abgebrochen = false;
    p.client.getDatevKonten(p.companyId, p.importId)
      .then((antwort) => { if (!abgebrochen) { setKonten(antwort); setFehler(null); } })
      .catch((e: unknown) => {
        if (abgebrochen) return;
        setKonten(null);
        setFehler(apiMeldung(e, "Die Konten konnten nicht geladen werden."));
      });
    return () => { abgebrochen = true; };
  }, [p.companyId, p.importId, p.client]);

  if (fehler !== null) return <Hinweis art="fehler">{fehler}</Hinweis>;
  if (konten === null) return <p className="dtv__hinweis" role="status">Lädt …</p>;

  return (
    <div className="dtv__konten">
      <Tabelle stapeln className="dtv__tabellenrahmen">
      <table className="dtv__tabelle">
        <caption>Offene Posten je Debitorenkonto</caption>
        <thead>
          <tr>
            <th scope="col">Konto</th><th scope="col" data-ausrichtung="rechts">Saldo</th>
          </tr>
        </thead>
        <tbody>
          {konten.offenePosten.map((z) => (
            <tr key={z.konto}>
              <td data-label="Konto">{z.konto}</td>
              <td data-label="Saldo" data-ausrichtung="rechts">{euro(z.saldo)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </Tabelle>
      <Tabelle stapeln className="dtv__tabellenrahmen">
      <table className="dtv__tabelle">
        <caption>Soll/Haben je Konto und Monat</caption>
        <thead>
          <tr>
            <th scope="col">Monat</th><th scope="col">Konto</th>
            <th scope="col" data-ausrichtung="rechts">Soll</th>
            <th scope="col" data-ausrichtung="rechts">Haben</th>
            <th scope="col" data-ausrichtung="rechts">Saldo</th>
          </tr>
        </thead>
        <tbody>
          {konten.monate.map((z) => (
            <tr key={`${z.monat}-${z.konto}`}>
              <td data-label="Monat">{z.monat}</td><td data-label="Konto">{z.konto}</td>
              <td data-label="Soll" data-ausrichtung="rechts">{euro(z.soll)}</td>
              <td data-label="Haben" data-ausrichtung="rechts">{euro(z.haben)}</td>
              <td data-label="Saldo" data-ausrichtung="rechts">{euro(z.saldo)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </Tabelle>
    </div>
  );
}

/* -------------------------------------------------------------------------------------- */
/* Die Modalpanels                                                                         */
/* -------------------------------------------------------------------------------------- */

type DialogPanelProps = {
  dialog: Dialog;
  companyId: string;
  client: DatevClient;
  fehler: string | null;
  beschaeftigt: boolean;
  setBeschaeftigt: (b: boolean) => void;
  setFehler: (m: string | null) => void;
  onAbbrechen: () => void;
  onFertig: () => void;
};

function DialogPanel(p: DialogPanelProps): ReactElement {
  const { dialog } = p;

  switch (dialog.art) {
    case "DEBITOREN_EINLESEN":
      return <DebitorenEinlesenPanel {...p} dialog={dialog} />;
    case "NUMMER_EINTRAGEN":
      return <NummerEintragenPanel {...p} dialog={dialog} />;
    case "KUNDE_ANLEGEN":
      return <KundeAnlegenPanel {...p} dialog={dialog} />;
    case "STAPEL_ERZEUGEN":
      return <StapelErzeugenPanel {...p} dialog={dialog} />;
    case "STAPEL_BESTAETIGEN":
      return <StapelBestaetigenPanel {...p} dialog={dialog} />;
    case "BUCHUNGEN_EINLESEN":
      return <BuchungenEinlesenPanel {...p} dialog={dialog} />;
    case "LAUF_VERWERFEN":
      return <LaufVerwerfenPanel {...p} dialog={dialog} />;
  }
}

/** Der Fehler eines Panels — IM Dialog, als Fehler-Hinweis (Rolle `alert` vom Element).
 *  Der Dialog schliesst bei einem Fehlschlag nie still: Die Auskunft gehört dorthin, wo
 *  die Handlung angestossen wurde. */
function PanelFehler({ text }: { text: string | null }): ReactElement | null {
  return text === null ? null : <Hinweis art="fehler">{text}</Hinweis>;
}

function DebitorenEinlesenPanel(
  p: DialogPanelProps & { dialog: Dialog & { art: "DEBITOREN_EINLESEN" } },
): ReactElement {
  const [standAt, setStandAt] = useState(p.dialog.standAt);
  const [datei, setDatei] = useState<File | null>(p.dialog.datei);
  const geaendert = datei !== null;

  async function abbrechen(): Promise<void> {
    if (p.beschaeftigt) return;
    if (!(await darfAbbrechen(geaendert))) return;
    p.onAbbrechen();
  }

  async function einlesen(): Promise<void> {
    if (datei === null || p.beschaeftigt) return;
    p.setBeschaeftigt(true); p.setFehler(null);
    try {
      await p.client.einlesenDatevDebitoren(
        p.companyId, datei, standAt.trim() === "" ? undefined : standAt.trim());
      p.onFertig();
    } catch (e) {
      p.setFehler(apiMeldung(e, "Die Datei liess sich nicht einlesen."));
    } finally {
      p.setBeschaeftigt(false);
    }
  }

  return (
    <Modal titel="Debitorendatei einlesen" onAbbrechen={abbrechen}
      abbrechenGesperrt={p.beschaeftigt}>
      <PanelFehler text={p.fehler} />
      {/* `laeuft` sperrt die Felder während des Einlesens (readOnly + aria-disabled, der
          Fokus bleibt) — auch die Dateiauswahl (`me-eingabe art="datei"`). */}
      <Formular beschriftung="oben" laeuft={p.beschaeftigt}>
        <Dateiwahl id="dtv-debitoren-datei" beschriftung="Datei" accept=".csv,.txt" pflicht
          onDateien={(dateien) => setDatei(dateien[0] ?? null)} />
        <Feld id="dtv-debitoren-stand" label="Stand" typ="date" bearbeitbar
          wert={standAt} onChange={setStandAt} />
      </Formular>
      <ModalFuss speichernText="Einlesen" speichernGesperrt={datei === null || p.beschaeftigt}
        abbrechenGesperrt={p.beschaeftigt}
        onSpeichern={() => { void einlesen(); }} onAbbrechen={abbrechen} />
    </Modal>
  );
}

/**
 * „Nummer eintragen" — ein Bearbeiten-Dialog mit EINEM Feld, seit ME-Etappe 3 (Aufgabe 7)
 * über `useFormularZustand` (Muster KundeBeziehungen, dieselbe Handlung an der
 * Kundenbeziehung): `laeuft` sperrt Feld und Fuss, eine 422 des Servers (`KOPPLUNG_AUS`,
 * `KREIS_ERSCHOEPFT`, `DEBITOR_AUSSERHALB`, `DEBITOR_VERGEBEN` mit Feldbezug) hängt AM
 * Feld, der Fokus springt dorthin. Eine 409 gibt es auf diesem Weg nicht.
 */
function NummerEintragenPanel(
  p: DialogPanelProps & { dialog: Dialog & { art: "NUMMER_EINTRAGEN" } },
): ReactElement {
  const [debtorNumber, setDebtorNumber] = useState(p.dialog.debtorNumber);
  const geaendert = debtorNumber.trim() !== "";

  const formular = useFormularZustand<true>({
    speichern: async () => {
      await p.client.setDebitorennummer(p.dialog.customerId, p.dialog.relationId,
        { modus: "WERT", debtorNumber: debtorNumber.trim() === "" ? null
          : debtorNumber.trim() });
      return true;
    },
    neuLaden: async () => { /* keine 409 auf diesem Weg */ },
    bekannteFelder: ["debtorNumber"],
    beschriftungen: { debtorNumber: "Debitorennummer" },
  });

  async function abbrechen(): Promise<void> {
    if (formular.laeuft) return;
    if (!(await darfAbbrechen(geaendert))) return;
    p.onAbbrechen();
  }

  async function eintragen(): Promise<void> {
    if (await formular.aktion() === true) p.onFertig();
  }

  return (
    <Modal titel={`Debitorennummer für ${p.dialog.name}`} onAbbrechen={abbrechen}
      abbrechenGesperrt={formular.laeuft}>
      <PanelFehler text={formular.meldung} />
      <Formular beschriftung="oben" {...formular.formularProps}>
        {/* Bewusst OHNE Bereichsprüfung hier: Die Vorgaben der Kopplung (Länge, Kreis)
            prüft der Server — sie sind je Unternehmen verschieden und änderbar. */}
        <Feld id="dtv-nummer" label="Debitorennummer" bearbeitbar wert={debtorNumber}
          fehler={formular.feldFehler.debtorNumber} onChange={setDebtorNumber} />
      </Formular>
      <ModalFuss speichernText="Eintragen" speichernGesperrt={formular.zustand !== "bereit"}
        abbrechenGesperrt={formular.laeuft}
        onSpeichern={() => { void eintragen(); }} onAbbrechen={abbrechen} />
    </Modal>
  );
}

function KundeAnlegenPanel(
  p: DialogPanelProps & { dialog: Dialog & { art: "KUNDE_ANLEGEN" } },
): ReactElement {
  function abbrechen(): void { if (!p.beschaeftigt) p.onAbbrechen(); }

  async function anlegen(): Promise<void> {
    if (p.beschaeftigt) return;
    p.setBeschaeftigt(true); p.setFehler(null);
    try {
      await p.client.legeKundeAusDatevDebitorAn(p.companyId, p.dialog.debtorNumber);
      p.onFertig();
    } catch (e) {
      p.setFehler(apiMeldung(e, "Der Kunde liess sich nicht anlegen."));
    } finally {
      p.setBeschaeftigt(false);
    }
  }

  return (
    <Modal titel="Kunden aus Debitor anlegen" onAbbrechen={abbrechen}
      abbrechenGesperrt={p.beschaeftigt}>
      <PanelFehler text={p.fehler} />
      <p className="dtv__dialogtext">
        Aus dem Debitor <strong>{p.dialog.debtorNumber}</strong> ({p.dialog.name}) wird ein
        neuer Kunde mit dessen Vorschlagsdaten angelegt und ihm sofort diese
        Debitorennummer eingetragen.
      </p>
      <ModalFuss speichernText="Anlegen" speichernGesperrt={p.beschaeftigt}
        abbrechenGesperrt={p.beschaeftigt}
        onSpeichern={() => { void anlegen(); }} onAbbrechen={abbrechen} />
    </Modal>
  );
}

function StapelErzeugenPanel(
  p: DialogPanelProps & { dialog: Dialog & { art: "STAPEL_ERZEUGEN" } },
): ReactElement {
  const [von, setVon] = useState(p.dialog.von);
  const [bis, setBis] = useState(p.dialog.bis);
  const geaendert = von.trim() !== "" || bis.trim() !== "";

  async function abbrechen(): Promise<void> {
    if (p.beschaeftigt) return;
    if (!(await darfAbbrechen(geaendert))) return;
    p.onAbbrechen();
  }

  async function erzeugen(): Promise<void> {
    if (von.trim() === "" || bis.trim() === "" || p.beschaeftigt) return;
    p.setBeschaeftigt(true); p.setFehler(null);
    try {
      await p.client.erzeugeDatevStapel(p.companyId, von, bis);
      p.onFertig();
    } catch (e) {
      p.setFehler(apiMeldung(e, "Der Stapel liess sich nicht erzeugen."));
    } finally {
      p.setBeschaeftigt(false);
    }
  }

  return (
    <Modal titel="Stapel erzeugen" onAbbrechen={abbrechen} abbrechenGesperrt={p.beschaeftigt}>
      <PanelFehler text={p.fehler} />
      <Formular beschriftung="oben" laeuft={p.beschaeftigt}>
        <Feld id="dtv-stapel-von" label="Von" typ="date" bearbeitbar pflicht
          wert={von} onChange={setVon} />
        <Feld id="dtv-stapel-bis" label="Bis" typ="date" bearbeitbar pflicht
          wert={bis} onChange={setBis} />
      </Formular>
      <ModalFuss speichernText="Erzeugen"
        speichernGesperrt={von.trim() === "" || bis.trim() === "" || p.beschaeftigt}
        abbrechenGesperrt={p.beschaeftigt}
        onSpeichern={() => { void erzeugen(); }} onAbbrechen={abbrechen} />
    </Modal>
  );
}

function StapelBestaetigenPanel(
  p: DialogPanelProps & { dialog: Dialog & { art: "STAPEL_BESTAETIGEN" } },
): ReactElement {
  function abbrechen(): void { if (!p.beschaeftigt) p.onAbbrechen(); }

  async function bestaetigen(): Promise<void> {
    if (p.beschaeftigt) return;
    p.setBeschaeftigt(true); p.setFehler(null);
    try {
      await p.client.bestaetigeDatevStapel(p.companyId, p.dialog.batchId);
      p.onFertig();
    } catch (e) {
      p.setFehler(apiMeldung(e, "Der Stapel liess sich nicht bestätigen."));
    } finally {
      p.setBeschaeftigt(false);
    }
  }

  return (
    <Modal titel="Stapel bestätigen" frage onAbbrechen={abbrechen} abbrechenGesperrt={p.beschaeftigt}>
      <PanelFehler text={p.fehler} />
      <p className="dtv__dialogtext">
        Der Stapel {p.dialog.zeitraum} wird als in DATEV übernommen bestätigt — samt
        allen seinen Posten.
      </p>
      <ModalFuss speichernText="Bestätigen" speichernGesperrt={p.beschaeftigt}
        abbrechenGesperrt={p.beschaeftigt}
        onSpeichern={() => { void bestaetigen(); }} onAbbrechen={abbrechen} />
    </Modal>
  );
}

function BuchungenEinlesenPanel(
  p: DialogPanelProps & { dialog: Dialog & { art: "BUCHUNGEN_EINLESEN" } },
): ReactElement {
  const [standAt, setStandAt] = useState(p.dialog.standAt);
  const [von, setVon] = useState(p.dialog.von);
  const [bis, setBis] = useState(p.dialog.bis);
  const [datei, setDatei] = useState<File | null>(p.dialog.datei);
  const geaendert = datei !== null;

  async function abbrechen(): Promise<void> {
    if (p.beschaeftigt) return;
    if (!(await darfAbbrechen(geaendert))) return;
    p.onAbbrechen();
  }

  async function einlesen(): Promise<void> {
    if (datei === null || von.trim() === "" || bis.trim() === "" || p.beschaeftigt) return;
    p.setBeschaeftigt(true); p.setFehler(null);
    try {
      await p.client.einlesenDatevBuchungen(p.companyId, datei, von, bis,
        standAt.trim() === "" ? undefined : standAt.trim());
      p.onFertig();
    } catch (e) {
      p.setFehler(apiMeldung(e, "Die Datei liess sich nicht einlesen."));
    } finally {
      p.setBeschaeftigt(false);
    }
  }

  return (
    <Modal titel="Buchungsdatei einlesen" onAbbrechen={abbrechen}
      abbrechenGesperrt={p.beschaeftigt}>
      <PanelFehler text={p.fehler} />
      {/* Datei als `me-eingabe art="datei"` (siehe Debitorendatei), Stand/Von/Bis als `Feld`. */}
      <Formular beschriftung="oben" laeuft={p.beschaeftigt}>
        <Dateiwahl id="dtv-buchungen-datei" beschriftung="Datei" accept=".csv,.txt" pflicht
          onDateien={(dateien) => setDatei(dateien[0] ?? null)} />
        <Feld id="dtv-buchungen-stand" label="Stand" typ="date" bearbeitbar
          wert={standAt} onChange={setStandAt} />
        <Feld id="dtv-buchungen-von" label="Von" typ="date" bearbeitbar pflicht
          wert={von} onChange={setVon} />
        <Feld id="dtv-buchungen-bis" label="Bis" typ="date" bearbeitbar pflicht
          wert={bis} onChange={setBis} />
      </Formular>
      <ModalFuss speichernText="Einlesen"
        speichernGesperrt={datei === null || von.trim() === "" || bis.trim() === ""
          || p.beschaeftigt}
        abbrechenGesperrt={p.beschaeftigt}
        onSpeichern={() => { void einlesen(); }} onAbbrechen={abbrechen} />
    </Modal>
  );
}

function LaufVerwerfenPanel(
  p: DialogPanelProps & { dialog: Dialog & { art: "LAUF_VERWERFEN" } },
): ReactElement {
  function abbrechen(): void { if (!p.beschaeftigt) p.onAbbrechen(); }

  async function verwerfen(): Promise<void> {
    if (p.beschaeftigt) return;
    p.setBeschaeftigt(true); p.setFehler(null);
    try {
      await p.client.verwerfeDatevBuchungsLauf(p.companyId, p.dialog.importId);
      p.onFertig();
    } catch (e) {
      p.setFehler(apiMeldung(e, "Der Lauf liess sich nicht verwerfen."));
    } finally {
      p.setBeschaeftigt(false);
    }
  }

  return (
    <Modal titel="Buchungslauf verwerfen" frage onAbbrechen={abbrechen}
      abbrechenGesperrt={p.beschaeftigt}>
      <PanelFehler text={p.fehler} />
      <p className="dtv__dialogtext">Dieser Lauf und seine Zeilen werden entfernt.</p>
      {/* Die Folge für bereits übernommene Zahlungen ist die eigentliche Warnung dieses
          Dialogs — als Warnung-Hinweis, nicht als fetter Satzteil im Fliesstext. */}
      {p.dialog.uebernommen > 0 && (
        <Hinweis art="warnung">
          Übernommene Zahlungen werden dabei zurückgenommen.
        </Hinweis>
      )}
      <ModalFuss speichernText="Verwerfen" speichernGesperrt={p.beschaeftigt}
        abbrechenGesperrt={p.beschaeftigt}
        onSpeichern={() => { void verwerfen(); }} onAbbrechen={abbrechen} />
    </Modal>
  );
}
